import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar, Avatar, Box, Button, Container, Divider, Drawer, IconButton,
  InputAdornment, InputBase, List, ListItemButton, ListItemIcon, ListItemText,
  Menu, MenuItem, ScopedCssBaseline, ThemeProvider, Toolbar, Typography,
  useMediaQuery,
} from '@mui/material';
import {
  BsBoxArrowInRight, BsBoxArrowRight, BsFileText, BsFolder2Open,
  BsHouseDoor, BsKanban, BsLifePreserver, BsList, BsPersonBadge,
  BsPersonPlus, BsSearch, BsXLg,
} from 'react-icons/bs';

import iziclaiMarque from '../../assets/iziclai_marque.png';
import iziclaiMot from '../../assets/iziclai_mot.png';
import { useAuth } from '../../context/AuthContext';
import { themeAssure } from '../../parcours/theme';

/**
 * Les entrées de menu, par rôle d'organisation.
 *
 * Les décrire en données plutôt qu'en JSX dupliqué évite la divergence qui
 * avait fini par faire pointer « Accueil » vers « / » pour l'un et « /tdb »
 * pour l'autre, sans que rien ne le dise.
 */
const MENUS = {
  NORMAL: [
    { to: '/mon-dossier', Icone: BsHouseDoor, libelle: 'Mon sinistre' },
  ],
  INTERMEDIAIRE: [
    { to: '/mon-dossier', Icone: BsFolder2Open, libelle: 'Mes dossiers' },
  ],
  EXPERT: [
    { to: '/tdb', Icone: BsHouseDoor, libelle: 'Accueil' },
    { to: '/expertise', Icone: BsFileText, libelle: 'Expertises' },
    { to: '/manager', Icone: BsKanban, libelle: 'Gestionnaire' },
  ],
};

const COMPTE = [
  { to: '/profil', Icone: BsPersonBadge, libelle: 'Mon profil et mes accès' },
  { to: '/aide', Icone: BsLifePreserver, libelle: "Besoin d'aide ?" },
];

const HAUTEUR = { xs: 58, md: 68 };

/**
 * Ouvre la recherche des cas similaires dans sa propre fenêtre, toujours la
 * même : on la garde à côté du dossier en cours, et une nouvelle recherche
 * la remplace au lieu d'empiler les fenêtres.
 */
export function ouvrirRecherche(requete) {
  const q = (requete || '').trim();
  const fenetre = window.open(
    `/recherche${q ? `?q=${encodeURIComponent(q)}` : ''}`, 'iziclai-recherche',
    'width=1000,height=900,scrollbars=yes,resizable=yes');
  if (fenetre) fenetre.focus();
}

function Header() {
  const location = useLocation();
  const { isAuthenticated, organization, user } = useAuth();
  const [tiroir, setTiroir] = useState(false);
  const [ancre, setAncre] = useState(null);
  const [recherche, setRecherche] = useState('');
  // `noSsrFallback` évite le premier rendu « mobile » qui faisait clignoter
  // le menu sur un grand écran au chargement.
  const compact = useMediaQuery(themeAssure.breakpoints.down('md'),
                                { noSsr: true });

  // Le menu déplié restait ouvert après le clic et masquait la page qu'on
  // venait de demander. Tout se referme au changement d'adresse.
  useEffect(() => { setTiroir(false); setAncre(null); }, [location.pathname]);

  const estActif = (chemin) =>
    location.pathname === chemin || location.pathname.startsWith(`${chemin}/`);

  const entrees = isAuthenticated ? (MENUS[organization?.role] || []) : [];
  const initiales = (user?.email || '?').slice(0, 2).toUpperCase();

  const marque = (
    <Box component={Link} to="/" aria-label="Iziclai — accueil"
         sx={{ display: 'inline-flex', alignItems: 'center', gap: 1,
               flexShrink: 0 }}>
      {/* Le symbole devant, le mot derrière. Le symbole seul est ce qu'on
          reconnaît de loin ; le mot n'est lisible que de près. */}
      <Box component="img" src={iziclaiMarque} alt=""
           sx={{ height: { xs: 30, md: 34 }, width: 'auto', display: 'block' }} />
      <Box component="img" src={iziclaiMot} alt="Iziclai"
           sx={{ height: { xs: 24, md: 28 }, width: 'auto', display: 'block' }} />
    </Box>
  );

  const identite = (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
        {user?.email}
      </Typography>
      {organization?.name && (
        <Typography variant="caption" color="text.secondary" noWrap
                    component="div">
          {organization.name}
        </Typography>
      )}
    </Box>
  );

  return (
    <ThemeProvider theme={themeAssure}>
      {/* Le CSS Bootstrap est importé globalement : cet écran s'en isole. */}
      <ScopedCssBaseline sx={{ bgcolor: 'transparent' }}>
        {/* `fixed` et non `sticky` : la barre est seule dans son conteneur,
            une barre collante n'y aurait aucune course à parcourir. Le vide
            qu'elle laisse est rendu juste en dessous. */}
        <AppBar
          position="fixed"
          elevation={0}
          color="inherit"
          sx={{
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Container maxWidth="lg" disableGutters>
            <Toolbar sx={{ minHeight: HAUTEUR, px: { xs: 2, md: 3 }, gap: 1 }}>
              {marque}

              {!compact && (
                <Box sx={{ display: 'flex', gap: 0.5, ml: 3, flexGrow: 1 }}>
                  {entrees.map(({ to, Icone, libelle }) => {
                    const actif = estActif(to);
                    return (
                      <Button
                        key={to}
                        component={Link}
                        to={to}
                        startIcon={<Icone />}
                        aria-current={actif ? 'page' : undefined}
                        sx={{
                          color: actif ? 'primary.main' : 'text.secondary',
                          bgcolor: actif ? 'action.selected' : 'transparent',
                          '&:hover': { bgcolor: 'action.hover',
                                       color: 'primary.main' },
                        }}
                      >
                        {libelle}
                      </Button>
                    );
                  })}
                </Box>
              )}

              <Box sx={{ flexGrow: compact ? 1 : 0 }} />

              {!isAuthenticated && !compact && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button component={Link} to="/login"
                          startIcon={<BsBoxArrowInRight />}
                          sx={{ color: 'text.secondary' }}>
                    Se connecter
                  </Button>
                  <Button component={Link} to="/signup" variant="contained"
                          startIcon={<BsPersonPlus />}>
                    Créer un compte
                  </Button>
                </Box>
              )}

              {organization?.role === 'EXPERT' && !compact && (
                <Box component="form"
                     onSubmit={(e) => {
                       e.preventDefault();
                       ouvrirRecherche(recherche);
                       setRecherche('');
                     }}
                     sx={{ mr: 1 }}>
                  <InputBase
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                    placeholder="Cas similaires…"
                    inputProps={{ 'aria-label': 'Rechercher dans les dossiers' }}
                    startAdornment={(
                      <InputAdornment position="start"
                                      sx={{ color: 'text.secondary' }}>
                        <BsSearch />
                      </InputAdornment>
                    )}
                    sx={{ width: { md: 190, lg: 240 }, px: 1.25, py: 0.25,
                          fontSize: '0.9rem', borderRadius: 1,
                          border: '1px solid', borderColor: 'divider',
                          bgcolor: 'background.default',
                          '&.Mui-focused': { borderColor: 'primary.main' } }}
                  />
                </Box>
              )}

              {isAuthenticated && !compact && (
                <>
                  <IconButton
                    onClick={(e) => setAncre(e.currentTarget)}
                    aria-label="Mon compte"
                    aria-haspopup="true"
                    aria-expanded={ancre ? 'true' : undefined}
                    sx={{ p: 0.5 }}
                  >
                    <Avatar sx={{ width: 36, height: 36, fontSize: '0.85rem',
                                  fontWeight: 600, bgcolor: 'primary.main' }}>
                      {initiales}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={ancre}
                    open={Boolean(ancre)}
                    onClose={() => setAncre(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{ paper: { sx: { minWidth: 260, mt: 1 } } }}
                  >
                    <Box sx={{ px: 2, py: 1 }}>{identite}</Box>
                    <Divider />
                    {COMPTE.map(({ to, Icone, libelle }) => (
                      <MenuItem key={to} component={Link} to={to}
                                onClick={() => setAncre(null)}>
                        <ListItemIcon><Icone /></ListItemIcon>
                        {libelle}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem component={Link} to="/logout"
                              onClick={() => setAncre(null)}
                              sx={{ color: 'error.main' }}>
                      <ListItemIcon sx={{ color: 'error.main' }}>
                        <BsBoxArrowRight />
                      </ListItemIcon>
                      Déconnexion
                    </MenuItem>
                  </Menu>
                </>
              )}

              {compact && (
                <IconButton onClick={() => setTiroir(true)} aria-label="Menu"
                            aria-expanded={tiroir}>
                  <BsList size={24} />
                </IconButton>
              )}
            </Toolbar>
          </Container>
        </AppBar>
        <Box sx={{ height: HAUTEUR }} aria-hidden="true" />

        {/* Sur téléphone, un tiroir plutôt qu'un dépliant : il recouvre la
            page au lieu de la repousser, et se ferme d'un geste. */}
        <Drawer
          anchor="right"
          open={tiroir}
          onClose={() => setTiroir(false)}
          slotProps={{ paper: { sx: { width: 290, maxWidth: '85vw' } } }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center',
                       justifyContent: 'space-between', px: 2,
                       minHeight: HAUTEUR.xs }}>
              {marque}
              <IconButton onClick={() => setTiroir(false)} aria-label="Fermer">
                <BsXLg />
              </IconButton>
            </Box>
            <Divider />

            {isAuthenticated && (
              <>
                <Box sx={{ px: 2, py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 40, height: 40, fontSize: '0.9rem',
                                  fontWeight: 600, bgcolor: 'primary.main' }}>
                      {initiales}
                    </Avatar>
                    {identite}
                  </Box>
                </Box>
                <Divider />
              </>
            )}

            <List sx={{ flexGrow: 1, py: 1 }}>
              {entrees.map(({ to, Icone, libelle }) => (
                <ListItemButton key={to} component={Link} to={to}
                                selected={estActif(to)}>
                  <ListItemIcon sx={{ minWidth: 40 }}><Icone /></ListItemIcon>
                  <ListItemText primary={libelle} />
                </ListItemButton>
              ))}

              {/* Sur téléphone, pas de seconde fenêtre : la page s'ouvre ici. */}
              {organization?.role === 'EXPERT' && (
                <ListItemButton component={Link} to="/recherche"
                                selected={estActif('/recherche')}>
                  <ListItemIcon sx={{ minWidth: 40 }}><BsSearch /></ListItemIcon>
                  <ListItemText primary="Cas similaires" />
                </ListItemButton>
              )}

              {isAuthenticated && COMPTE.map(({ to, Icone, libelle }) => (
                <ListItemButton key={to} component={Link} to={to}
                                selected={estActif(to)}>
                  <ListItemIcon sx={{ minWidth: 40 }}><Icone /></ListItemIcon>
                  <ListItemText primary={libelle} />
                </ListItemButton>
              ))}

              {!isAuthenticated && (
                <>
                  <ListItemButton component={Link} to="/login">
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <BsBoxArrowInRight />
                    </ListItemIcon>
                    <ListItemText primary="Se connecter" />
                  </ListItemButton>
                  <ListItemButton component={Link} to="/signup">
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <BsPersonPlus />
                    </ListItemIcon>
                    <ListItemText primary="Créer un compte" />
                  </ListItemButton>
                </>
              )}
            </List>

            {isAuthenticated && (
              <>
                <Divider />
                <ListItemButton component={Link} to="/logout"
                                sx={{ color: 'error.main', py: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
                    <BsBoxArrowRight />
                  </ListItemIcon>
                  <ListItemText primary="Déconnexion" />
                </ListItemButton>
              </>
            )}
          </Box>
        </Drawer>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
}

export default Header;
